import { useEffect, useState } from 'react';
import { useSubjectLanguageVariants } from '@features/user';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import type {
  ProviderWaitingRoomRemoveRequest,
  WaitingRoomEntry,
  WaitingRoomRemovedReason,
} from '../types';

interface RemoveReasonOption {
  value: WaitingRoomRemovedReason;
  label: string;
}

const REMOVE_REASON_OPTIONS: RemoveReasonOption[] = [
  { value: 'misdirected', label: 'Misdirected' },
  { value: 'inappropriate', label: 'Inappropriate' },
  { value: 'other', label: 'Other' },
];

interface WaitingRoomRemoveDialogProps {
  open: boolean;
  entry: WaitingRoomEntry | null;
  isSubmitting?: boolean;
  container?: HTMLElement | null;
  onClose: () => void;
  onConfirm: (request: ProviderWaitingRoomRemoveRequest) => Promise<void> | void;
}

export function WaitingRoomRemoveDialog({
  open,
  entry,
  isSubmitting = false,
  container,
  onClose,
  onConfirm,
}: WaitingRoomRemoveDialogProps) {
  const { singular } = useSubjectLanguageVariants();
  const [reason, setReason] = useState<WaitingRoomRemovedReason>('misdirected');
  const [reasonDetail, setReasonDetail] = useState('');

  useEffect(() => {
    if (open) {
      setReason('misdirected');
      setReasonDetail('');
    }
  }, [open, entry?.waiting_room_queue_entry_id]);

  const displayName = entry?.patient_name || `this ${singular}`;

  const handleConfirm = () => {
    const trimmedDetail = reasonDetail.trim();
    const request: ProviderWaitingRoomRemoveRequest = {
      reason,
      ...(reason === 'other' && trimmedDetail ? { reason_detail: trimmedDetail } : {}),
    };

    void onConfirm(request);
  };

  return (
    <Dialog
      open={open}
      onClose={isSubmitting ? undefined : onClose}
      container={container ?? undefined}
      fullWidth
      maxWidth="xs"
    >
      <DialogTitle>Remove from waiting room?</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Typography>
            This removes {displayName} from the waiting room. They will need to check in again if
            they still need to be seen.
          </Typography>

          <FormControl>
            <FormLabel id="waiting-room-remove-reason-label">Reason</FormLabel>
            <RadioGroup
              aria-labelledby="waiting-room-remove-reason-label"
              value={reason}
              onChange={event => setReason(event.target.value as WaitingRoomRemovedReason)}
            >
              {REMOVE_REASON_OPTIONS.map(option => (
                <FormControlLabel
                  key={option.value}
                  value={option.value}
                  control={<Radio />}
                  label={option.label}
                />
              ))}
            </RadioGroup>
          </FormControl>

          {reason === 'other' && (
            <TextField
              label="Optional details"
              value={reasonDetail}
              onChange={event => setReasonDetail(event.target.value)}
              multiline
              minRows={3}
              slotProps={{ htmlInput: { maxLength: 500 } }}
              helperText={`${reasonDetail.length}/500`}
              disabled={isSubmitting}
            />
          )}

          <Alert severity="info">
            Keep details brief and avoid unnecessary sensitive information.
          </Alert>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleConfirm}
          disabled={!entry || isSubmitting}
        >
          Remove
        </Button>
      </DialogActions>
    </Dialog>
  );
}
